import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Helpers from "../Config/Helpers";
import { IconButton, Box } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import ModuleLayout from "./ModuleLayout/ModuleLayout";
import {
  buildCustomerPayload,
  createCustomerInitialValues,
  customerValidationSchema,
  getCustomerFormFields,
  prepareCustomerFormValues,
  resolveCustomerType,
} from "./Customers/customerFormConfig";
import { formatCustomerAddress } from "../utils/customerAddress";

function Customers() {
  const [activeTab, setActiveTab] = useState("individual");
  const customerInit = useMemo(
    () => ({
      ...createCustomerInitialValues(Helpers.authParentId),
      customer_type: activeTab,
    }),
    [activeTab],
  );
  const customerFields = getCustomerFormFields();
  const customerTabs = [
    {
      value: "individual",
      label: "Individuals",
      icon: <PersonIcon fontSize="small" />,
    },
    {
      value: "business",
      label: "Business",
      icon: <BusinessIcon fontSize="small" />,
    },
  ];
  const formatDiscount = (customer = {}) => {
    if (!customer.discountIncluded || !customer.discountType || !Number(customer.discount)) {
      return "";
    }
    return customer.discountType === "Percentage"
      ? `${customer.discount}%`
      : `$${Number(customer.discount).toFixed(2)}`;
  };
  const digitsOnly = (value = "") => String(value || "").replace(/\D/g, "");

  // Custom search filter
  const searchFilter = (customer, search) => {
    if (resolveCustomerType(customer) !== activeTab) return false;
    if (!search) return true;
    const term = search.toLowerCase();
    const searchDigits = digitsOnly(search);
    const phoneDigits = digitsOnly(customer.phone);
    const phoneMatches =
      (customer.phone || "").toLowerCase().includes(term) ||
      (searchDigits && phoneDigits.includes(searchDigits));

    return (
      (customer.name || "").toLowerCase().includes(term) ||
      (customer.company_name || "").toLowerCase().includes(term) ||
      (customer.dealer_no || "").toLowerCase().includes(term) ||
      (customer.tax_exempt_number || "").toLowerCase().includes(term) ||
      formatDiscount(customer).toLowerCase().includes(term) ||
      (customer.email || "").toLowerCase().includes(term) ||
      phoneMatches ||
      formatCustomerAddress(customer).toLowerCase().includes(term)
    );
  };

  // Custom sort function
  const sortFunction = (a, b) =>
    (a.name || "").localeCompare(b.name || "", undefined, {
      sensitivity: "base",
    });

  // Get item name for dialogs
  const getItemName = (customer) => customer?.name;

  // Data Grid columns configuration
  const columns = [
    {
      field: "serial",
      headerName: "Sr. #",
      width: 80,
      sortable: false,
      filterable: false,
    },
    {
      field: "name",
      headerName: activeTab === "business" ? "Business Name" : "Customer Name",
      flex: 1,
      minWidth: 150,
    },
    ...(activeTab === "business"
      ? [
          {
            field: "dealer_no",
            headerName: "Dealer No",
            width: 130,
          },
          {
            field: "tax_exempt_number",
            headerName: "Tax Exempt #",
            width: 150,
          },
        ]
      : []),
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "phone",
      headerName: "Phone",
      width: 130,
    },
    {
      field: "discount",
      headerName: "Discount",
      width: 120,
      valueGetter: (_, row) => formatDiscount(row),
    },
    {
      field: "address",
      headerName: "Address",
      flex: 1,
      minWidth: 200,
      valueGetter: (_, row) => formatCustomerAddress(row),
    },
  ];

  // Custom actions renderer with view button
  const customActionsRenderer = (
    params,
    canUpdate,
    canDelete,
    handleEdit,
    handleDelete,
  ) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        width: "100%",
        gap: 1,
      }}
    >
      <IconButton
        component={Link}
        to={`/user/receipts/customer/${params.row.name}/${params.row._id}`}
        color="warning"
        size="small"
      >
        <VisibilityIcon fontSize="small" />
      </IconButton>
      {canUpdate && (
        <IconButton
          onClick={() => handleEdit(params.row)}
          color="success"
          size="small"
        >
          <EditIcon fontSize="small" />
        </IconButton>
      )}
      {canDelete && (
        <IconButton
          onClick={() => handleDelete(params.row)}
          color="error"
          size="small"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );

  return (
    <ModuleLayout
      moduleName="Customers"
      moduleKey="customers"
      icon={require("../images/icons/clients.png")}
      initialFormValues={customerInit}
      formFields={customerFields}
      validationSchema={customerValidationSchema}
      onBeforeSave={(values) =>
        buildCustomerPayload(values, Helpers.authParentId)
      }
      mapItemToFormValues={(item) =>
        prepareCustomerFormValues(item, Helpers.authParentId)
      }
      columns={columns}
      searchFilter={searchFilter}
      sortFunction={sortFunction}
      getItemName={getItemName}
      customActionsRenderer={customActionsRenderer}
      deleteWarning="This action cannot be undone. All associated records and receipts will remain but will no longer be linked to this customer."
      tabs={customerTabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    />
  );
}

export default Customers;
